import {Component, inject, input, output} from '@angular/core';
import {ImagesService} from '../../../services/general/images.service';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {PrimaryButtonComponent} from '../../buttons/primary-button/primary-button.component';
import {Institution} from '../../../interfaces/responses/institution.interface';
import {InstitutionService} from '../../../services/institution/institution.service';
import {ToastService} from '../../../services/general/toast.service';
import {ProgressLoaderComponent} from '../../loaders/progress-loader/progress-loader.component';
import {AdminService} from '../../../services/admin/admin.service';
import {ProductService} from '../../../services/products/product.service';
import {Product} from '../../../interfaces/responses/product-response.interfaces';
import {GenerateApiKeyRequest} from '../../../interfaces/requests/api-keys.interfaces';

@Component({
  selector: 'app-generate-api-key',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PrimaryButtonComponent,
    ProgressLoaderComponent
  ],
  templateUrl: './generate-api-key.component.html',
  styleUrl: './generate-api-key.component.scss'
})
export class GenerateApiKeyComponent {
  _images = inject(ImagesService);
  _fb = inject(FormBuilder);
  institutionService = inject(InstitutionService);
  productService = inject(ProductService);
  _toast = inject(ToastService);
  adminService = inject(AdminService);

  isLoading: boolean = false;
  isLoadingInstitutions: boolean = false;
  isLoadingProducts: boolean = false;
  institutions: Institution[] = [];
  products: Product[] = [];

  generateApiKeyForm: FormGroup = this._fb.group({
    institutionId: ['', [Validators.required]],
    productId: ['', [Validators.required]],
    allowedOrigins: [''],
  });

  onCloseEvent = output<void>();
  onApiKeyGenerated = output<void>();

  ngOnInit(): void {
    this.loadInstitutions();

    this.generateApiKeyForm.get('institutionId')!.valueChanges.subscribe((institutionId) => {
      this.generateApiKeyForm.get('productId')!.setValue('');
      this.products = [];
      if (institutionId) {
        this.loadProducts(institutionId);
      }
    });
  }

  loadInstitutions(): void {
    this.isLoadingInstitutions = true;
    this.institutionService.getPaginatedInstitutions({
      paginate: false,
    }).subscribe({
      next: (response) => {
        this.institutions = response.data || [];
        this.isLoadingInstitutions = false;
      },
      error: (error) => {
        console.error('Error fetching institutions:', error);
        this._toast.error('Error loading institutions');
        this.isLoadingInstitutions = false;
      }
    });
  }

  loadProducts(institutionId: string): void {
    this.isLoadingProducts = true;
    this.productService.getPaginatedProducts({
      institutionId,
      paginate: false,
    }).subscribe({
      next: (response) => {
        this.products = response.data || [];
        this.isLoadingProducts = false;
      },
      error: (error) => {
        console.error('Error fetching products:', error);
        this._toast.error('Error loading products');
        this.isLoadingProducts = false;
      }
    });
  }

  onClose(): void {
    this.onCloseEvent.emit();
  }

  parseCommaSeparated(value: string): string[] {
    return (value || '')
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }

  onSubmit(): void {
    if (this.generateApiKeyForm.valid) {
      const { institutionId, productId, allowedOrigins } = this.generateApiKeyForm.value;
      const payload: GenerateApiKeyRequest = {
        institutionId,
        productId,
        allowedOrigins: this.parseCommaSeparated(allowedOrigins),
      };
      this.isLoading = true;

      this.adminService.generateApiKey(payload).subscribe({
        next: (response) => {
          this._toast.success('API key generated successfully');
          this.isLoading = false;
          this.onApiKeyGenerated.emit();
          this.onClose();
        },
        error: (error) => {
          console.error('Error generating API key:', error);
          this._toast.error('Error generating API key');
          this.isLoading = false;
        }
      });
    } else {
      this.generateApiKeyForm.markAllAsTouched();
      this._toast.error('Please fill in all required fields');
    }
  }
}
